import React from 'react';
import { styled } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { ExpandableSubmenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/expandable-submenu-item/index.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { matchPartialUrlPathname } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { useWorkCategoriesAllSupportedWithForceRefreshOnMount } from '@atlassian/jira-router-resources-service-desk-practices/src/controllers/index.tsx';
import { REQUEST_TYPE_PRACTICE_NAV_ONBOARDING } from '@atlassian/jira-servicedesk-common/src/constants.tsx';
import GlobalSpotlightTargetDeprecated from '@atlassian/jira-servicedesk-common/src/ui/global-spotlight-target/index.tsx';
import { toShortFormItsmPractice } from '@atlassian/jira-servicedesk-work-category/src/controllers/to-work-category-short-form/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import { SETTINGS_IDS } from '../../../constants.tsx';
import { useLegacySpaClickHandler } from '../../../utils/index.tsx';
import messages from '../messages.tsx';

const requestTypesGroupWrapperStyles = xcss({
	marginLeft: 'space.150',
});

const requestTypesWrapperStyles = xcss({
	marginLeft: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const StyledSpan = styled.span({
	paddingLeft: token('space.200'),
});

type TicketTypesItemProps = {
	nestingId: string;
	projectKey: string;
};

export const TicketTypesItem = ({ nestingId, projectKey }: TicketTypesItemProps) => {
	const { formatMessage } = useIntl();
	const enabledPracticeData = useWorkCategoriesAllSupportedWithForceRefreshOnMount();
	const handleLegacySPAClick = useLegacySpaClickHandler();
	const enabledPractices = enabledPracticeData.data || [];
	const [{ location }] = useRouter();

	return enabledPractices.length > 0 ? (
		<Box xcss={requestTypesGroupWrapperStyles}>
			<ExpandableSubmenuItem
				analytics={{ itemId: SETTINGS_IDS.TICKET_TYPES }}
				menuItemLabel={formatMessage(messages.requestTypes)}
				submenuId={nestingId}
				forceExpand={location.pathname.startsWith(
					`/jira/servicedesk/projects/${projectKey}/settings/request-types/`,
				)}
			>
				{/* NOTE: This GlobalSpotlightTarget (deprecated) can be used throughout Jira not just within
				 * the current package. They're often used for onboarding or changeboarding
				 * experiences and allow an Atlaskit spotlight to be attached to page elements.
				 * If you need to change this, make sure you check where the target ID is being used
				 * and contact the relevant team as necessary. This Sourcegraph link should help :)
				 * http://go/src/TICKET_TYPES_SPOTLIGHT|sd-ticket-types-spotlight
				 */}
				<GlobalSpotlightTargetDeprecated
					id={REQUEST_TYPE_PRACTICE_NAV_ONBOARDING.PRACTICE_NAV_SPOTLIGHT}
				>
					<Box xcss={requestTypesWrapperStyles}>
						{enabledPractices.filter(Boolean).map((practice) => (
							<MenuItem
								onClick={handleLegacySPAClick}
								key={practice}
								analytics={{
									itemId: `sd-ticket-types-${toShortFormItsmPractice(practice)}`,
								}}
								href={`/jira/servicedesk/projects/${projectKey}/settings/request-types/category/${toShortFormItsmPractice(
									practice,
								)}`}
								selectedOn={matchPartialUrlPathname()}
							>
								<StyledSpan>{formatMessage(messages[`practice_${practice}`])}</StyledSpan>
							</MenuItem>
						))}
					</Box>
				</GlobalSpotlightTargetDeprecated>
				<GlobalSpotlightTargetDeprecated
					id={REQUEST_TYPE_PRACTICE_NAV_ONBOARDING.UNASSIGNED_NAV_SPOTLIGHT}
				>
					<Box xcss={requestTypesWrapperStyles}>
						<MenuItem
							onClick={__SPA__ ? undefined : handleLegacySPAClick}
							analytics={{
								itemId: 'sd-ticket-types-unassigned',
							}}
							href={`/jira/servicedesk/projects/${projectKey}/settings/request-types/unassigned`}
							selectedOn={matchPartialUrlPathname()}
						>
							<StyledSpan>{formatMessage(messages.unassigned)}</StyledSpan>
						</MenuItem>
					</Box>
				</GlobalSpotlightTargetDeprecated>
			</ExpandableSubmenuItem>
		</Box>
	) : (
		<MenuItem
			analytics={{ itemId: SETTINGS_IDS.TICKET_TYPES }}
			selectedOn={matchPartialUrlPathname()}
			href={`/jira/servicedesk/projects/${projectKey}/settings/request-types`}
			onClick={handleLegacySPAClick}
		>
			{formatMessage(messages.requestTypes)}
		</MenuItem>
	);
};
